/**
 * The one `fetch` every server-side Supabase client uses (server, admin,
 * middleware). It exists so a stalled database fails fast instead of holding
 * a Vercel function until its 300 s limit (ALU-25), and so a write is never
 * sent twice.
 *
 * Rules:
 * - Every attempt has a timeout, and the whole call (attempts plus backoff)
 *   has a budget. REST/Auth: 10 s per attempt, 25 s in total. Storage: 60 s
 *   per attempt, 90 s in total, so a large upload is not cut off.
 * - Only GET/HEAD are retried: on 429/502/503, on a network error, or when
 *   an attempt timed out, as long as the budget allows. A 504 is never
 *   retried: it means the gateway already waited for Postgres, and asking
 *   again only doubles the wait.
 * - POST/PATCH/PUT/DELETE (including PostgREST `.rpc()`, which is a POST) are
 *   sent once. A 5xx can arrive after Postgres committed, so a resend could
 *   insert the row twice. The one exception is a connection that failed
 *   before the request left (DNS failure, connection refused, connect
 *   timeout) and has a replayable body: nothing reached the server.
 * - A caller-provided signal (`.abortSignal()`) still aborts the call, and is
 *   never retried.
 *
 * Why 10 s: the `authenticated` role runs with an 8 s `statement_timeout`, so
 * a slow-but-legitimate query comes back as a Postgres error before the
 * client gives up; a healthy PostgREST read answers in well under a second.
 * Why 25 s: at most two full attempts plus backoff, far below the 300 s
 * function limit, and short enough that the user sees an error page instead
 * of a spinner.
 *
 * The timers are plain `setTimeout`s (unref'd) rather than
 * `AbortSignal.timeout`, so tests can drive them with fake timers. They are
 * left running after a response arrives on purpose: the caller reads the body
 * next, and a body that stalls mid-stream is covered by the same deadline.
 */

type FetchFn = typeof fetch

export interface SupabaseFetchOptions {
  /** Underlying fetch. Defaults to the global `fetch`; tests inject a fake. */
  fetch?: FetchFn
  /** Retries after the first attempt, for requests that may be retried. */
  retries?: number
  /** Backoff before retry n (0-based) is `retryBaseDelayMs * 2^n`. */
  retryBaseDelayMs?: number
  /** Timeout for one REST/Auth attempt. */
  attemptTimeoutMs?: number
  /** Budget for one REST/Auth call, attempts and backoff included. */
  totalBudgetMs?: number
  /** Timeout for one Storage attempt (`/storage/v1/`). */
  storageAttemptTimeoutMs?: number
  /** Budget for one Storage call. */
  storageTotalBudgetMs?: number
}

type SupabaseFetchConfig = Required<Omit<SupabaseFetchOptions, 'fetch'>>

export const SUPABASE_FETCH_DEFAULTS: Readonly<SupabaseFetchConfig> = {
  retries: 3,
  retryBaseDelayMs: 300,
  attemptTimeoutMs: 10_000,
  totalBudgetMs: 25_000,
  storageAttemptTimeoutMs: 60_000,
  storageTotalBudgetMs: 90_000,
}

const RETRYABLE_STATUSES = new Set([429, 502, 503])
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD'])
/** Error codes that mean the connection never opened, so nothing was sent. */
const PRE_SEND_ERROR_CODES = new Set([
  'ECONNREFUSED',
  'ENOTFOUND',
  'EAI_AGAIN',
  'UND_ERR_CONNECT_TIMEOUT',
])

export class SupabaseFetchTimeoutError extends Error {
  override name = 'TimeoutError'
}

export function createSupabaseFetch(options: SupabaseFetchOptions = {}): FetchFn {
  const cfg = { ...SUPABASE_FETCH_DEFAULTS, ...options }

  return async function supabaseFetch(input, init) {
    const baseFetch = options.fetch ?? fetch
    const method = requestMethod(input, init)
    const url = requestUrl(input)
    const isStorage = url.includes('/storage/v1/')
    const attemptTimeoutMs = isStorage ? cfg.storageAttemptTimeoutMs : cfg.attemptTimeoutMs
    const totalBudgetMs = isStorage ? cfg.storageTotalBudgetMs : cfg.totalBudgetMs
    const idempotent = IDEMPOTENT_METHODS.has(method)
    const callerSignal = init?.signal ?? (isRequest(input) ? input.signal : undefined)
    const label = `${method} ${safePath(url)}`

    const deadline = Date.now() + totalBudgetMs
    const budget = timeoutController(
      totalBudgetMs,
      `Supabase request exceeded its ${totalBudgetMs} ms budget (${label})`
    )

    for (let attempt = 0; ; attempt++) {
      const remaining = deadline - Date.now()
      const attemptTimer = timeoutController(
        Math.min(attemptTimeoutMs, remaining),
        `Supabase request timed out after ${Math.min(attemptTimeoutMs, remaining)} ms (${label})`
      )
      const signal = anySignal(
        [callerSignal, budget.signal, attemptTimer.signal].filter(
          (s): s is AbortSignal => s != null
        )
      )

      let response: Response
      try {
        response = await baseFetch(input, { ...init, signal })
      } catch (error) {
        attemptTimer.clear()
        // The caller gave up, or the whole budget is spent: stop here.
        if (callerSignal?.aborted) {
          budget.clear()
          throw error
        }
        if (budget.signal.aborted) throw budget.signal.reason
        // An attempt that timed out surfaces as our named TimeoutError, not
        // as the runtime's generic AbortError.
        const failure = attemptTimer.signal.aborted ? attemptTimer.signal.reason : error
        const retryable = idempotent || (isPreSendError(error) && hasReplayableBody(init))
        if (!retryable || !(await canRetry(attempt, cfg, deadline))) {
          budget.clear()
          throw failure
        }
        continue
      }

      if (!idempotent || !RETRYABLE_STATUSES.has(response.status)) {
        return softenRefreshFailure(method, url, response)
      }
      if (!(await canRetry(attempt, cfg, deadline, response))) {
        return softenRefreshFailure(method, url, response)
      }
      attemptTimer.clear()
    }
  }
}

/**
 * auth-js drops the stored session (and so the cookie) when a refresh fails
 * with anything it does not consider "retryable": only network errors and
 * 502/503/504 are (auth-js 2.89 `handleError`, `_callRefreshToken`). A 500 or
 * a 429 from `/auth/v1/token` therefore signs the person out for good although
 * the refresh token is still valid. Re-labelling those answers as 503 makes
 * auth-js keep the session and report a retryable error; the next request
 * refreshes again. 4xx answers that really mean "this token is dead"
 * (400 `invalid_grant`, `refresh_token_not_found`, `refresh_token_already_used`)
 * are left alone.
 *
 * Exported for the browser client, which has no other fetch wrapper.
 */
export function softenRefreshFailure(method: string, url: string, response: Response): Response {
  if (method !== 'POST') return response
  const status = response.status
  const transient = status === 429 || (status >= 500 && !AUTH_JS_RETRYABLE_STATUSES.has(status))
  if (!transient || !isRefreshTokenRequest(url)) return response
  const headers = new Headers(response.headers)
  headers.set('x-original-status', String(status))
  return new Response(response.body, { status: 503, statusText: 'Service Unavailable', headers })
}

/** The statuses auth-js already treats as retryable. */
const AUTH_JS_RETRYABLE_STATUSES = new Set([502, 503, 504])

function isRefreshTokenRequest(url: string): boolean {
  try {
    const u = new URL(url)
    return (
      u.pathname.endsWith('/auth/v1/token') && u.searchParams.get('grant_type') === 'refresh_token'
    )
  } catch {
    return false
  }
}

/** Waits the backoff and returns true when another attempt fits the budget. */
async function canRetry(
  attempt: number,
  cfg: SupabaseFetchConfig,
  deadline: number,
  discard?: Response
): Promise<boolean> {
  if (attempt >= cfg.retries) return false
  const delay = cfg.retryBaseDelayMs * 2 ** attempt
  // Not worth starting an attempt with less than a second left.
  if (Date.now() + delay + 1_000 > deadline) return false
  // Free the connection held by the response we are about to drop.
  await discard?.body?.cancel().catch(() => {})
  await new Promise((resolve) => setTimeout(resolve, delay))
  return true
}

function timeoutController(ms: number, message: string) {
  const controller = new AbortController()
  const timer = setTimeout(
    () => controller.abort(new SupabaseFetchTimeoutError(message)),
    Math.max(0, ms)
  )
  ;(timer as { unref?: () => void }).unref?.()
  return { signal: controller.signal, clear: () => clearTimeout(timer) }
}

/** `AbortSignal.any` where it exists (Node >= 20.3); a listener fallback elsewhere. */
function anySignal(signals: AbortSignal[]): AbortSignal {
  if (typeof AbortSignal.any === 'function') return AbortSignal.any(signals)
  const controller = new AbortController()
  for (const s of signals) {
    if (s.aborted) {
      controller.abort(s.reason)
      break
    }
    s.addEventListener('abort', () => controller.abort(s.reason), { once: true })
  }
  return controller.signal
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  return (init?.method ?? (isRequest(input) ? input.method : 'GET')).toUpperCase()
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return (input as Request).url
}

/** Path only: query strings carry filter values, which can be personal data. */
function safePath(url: string): string {
  try {
    return new URL(url).pathname
  } catch {
    return url.split('?')[0] ?? url
  }
}

function isPreSendError(error: unknown): boolean {
  const cause = (error as { cause?: { code?: unknown } } | null)?.cause
  const code = cause?.code ?? (error as { code?: unknown } | null)?.code
  return typeof code === 'string' && PRE_SEND_ERROR_CODES.has(code)
}

function hasReplayableBody(init?: RequestInit): boolean {
  return !(typeof ReadableStream !== 'undefined' && init?.body instanceof ReadableStream)
}

function isRequest(input: RequestInfo | URL): input is Request {
  return typeof Request !== 'undefined' && input instanceof Request
}
